import { inject, Injectable } from '@angular/core';
import { App } from '@capacitor/app';
import { AppConfigService } from '@config/app-config.service';
import { ContentService } from '@content/content.service';
import { DeviceService } from '@device/device.service';
import { environment } from '@environments/environment';
import { EventService } from '@event/event.service';
import { FolloweeService } from '@followee/followee.service';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@notification/notification.service';
import { PushNotificationService } from '@notification/push-notification.service';
import { PhotoAlbumService } from '@photo/photo-album.service';
import { PhotoService } from '@photo/photo.service';
import * as Sentry from "@sentry/angular";
import { Constants } from '@shared/constants';
import { catchError, forkJoin, map, Observable, of, mergeMap } from 'rxjs';


@Injectable({ providedIn: 'root' })
export class AppInitService {
  private appConfigService = inject(AppConfigService);
  private contentService = inject(ContentService);
  private deviceService = inject(DeviceService);
  private eventService = inject(EventService);
  private notificationService = inject(NotificationService);
  private followeeService = inject(FolloweeService);
  private photoAlbumService = inject(PhotoAlbumService);
  private photoService = inject(PhotoService);
  private pushNotificationService = inject(PushNotificationService);
  private translateService = inject(TranslateService);

  initialize(): Observable<boolean> {
    this.translateService.setFallbackLang(Constants.LANGUAGE_CODE);

    void App.addListener('resume', () => this.onResume().subscribe());

    // This AppInit logic runs on startup of the app, during the splashscreen. This can not fail!
    //
    // All of the init() childs have to have proper error handling so the process will always continue on startup
    // Also the logic that needs to be runsat startup has to have an option to re-run.
    // Based on a timer, a signal or something else
    //
    // Check the called init() methods for more details on the logic
    // Order is important! Do not mix the order of the init() methods!
    return this.appConfigService.init().pipe(
      mergeMap(() => this.eventService.init()),
      mergeMap(() => this.translateService.use(Constants.LANGUAGE_CODE)),
      mergeMap(() =>
        forkJoin([
          this.contentService.init(),
          this.notificationService.init(),
          this.photoService.init(),
          this.photoAlbumService.init(),
        ]),
      ),
      mergeMap(() => this.deviceService.init()),
      mergeMap(() => this.followeeService.init()),
      mergeMap(followees => this.pushNotificationService.init(followees)),
      mergeMap(() => this.initializeApp()),
      catchError(error => {
        console.error('[AppInitService] error', error);
        return of(false);
      }),
    );
  }

  private initializeApp(): Observable<boolean> {
    if (environment.sentry) {
      const deviceInfo = this.deviceService.deviceInfo();
      const appConfig = this.appConfigService.appConfig();
      const appIdentier = this.appConfigService.appIdentifier();

      const release = deviceInfo && deviceInfo.appVersion !== 'version-not-set' ? deviceInfo.appVersion : undefined;

      Sentry.init({
        dsn: environment.sentry.dsn,
        environment: environment.name.toLowerCase(),
        release
      });

      if (appIdentier) {
        Sentry.captureMessage('App initialized',
          {
            level: 'info',
            tags: {
              // eslint-disable-next-line @typescript-eslint/naming-convention
              'app.code': appConfig?.code
            },
            user: { id: appIdentier.uuid }
          });
      }
    }

    return of(true)
  }

  private onResume(): Observable<void> {
    return forkJoin([
      this.appConfigService.onResume(),
      this.contentService.onResume(),
      this.eventService.onResume(),
      this.followeeService.onResume(),
      this.photoAlbumService.onResume(),
    ]).pipe(
      map(() => undefined),
    );
  }
}
